/**
 *
 * @module commerceClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/commerce.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import BillingAddress from './billingAddress';
import CommerceClientConfiguration from './commerceClientConfiguration';
import CommerceClientEndpoint from './commerceClientEndpoint';
import CreatePaymentMethodRequest from './createPaymentMethodRequest';
import CreatePaymentMethodResponse from './createPaymentMethodResponse';
import GuidGeneratorResponse from './guidGeneratorResponse';
import OrderItem from './orderItem';
import OrderStatusResponse from './orderStatusResponse';
import PaymentMethodResponse from './paymentMethodResponse';
import Price from './price';
import PriceOrderRequest from './priceOrderRequest';
import PriceOrderResponse from './priceOrderResponse';
import Sku from './sku';
import SubmitOrderRequest from './submitOrderRequest';
import SubmitOrderWithPaymentMethodRequest from './submitOrderWithPaymentMethodRequest';
import SubmitOrderWithPaymentMethodResponse from './submitOrderWithPaymentMethodResponse';
import ZipLocation from './zipLocation';

const AccessToken = Token.AccessToken;
const CommerceClientDustUrnReference = DustUrnReference.services.commerce.commerceClient;
const HttpMethod = Configuration.HttpMethod;

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const {
    number,
    nonEmptyObject,
    nonEmptyString,
    instanceStrict
} = Check.assert;

/**
 *
 * @class CommerceClient
 * @desc Provides a data client that can be used to access commerce services.
 *
 */
export default class CommerceClient {

    /**
     *
     * @param {SDK.Services.Commerce.CommerceClientConfiguration} commerceClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(commerceClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: `${this.constructor.name}(commerceClientConfiguration, logger, httpClient)`,
            get commerceClientConfiguration() {
                return `${this.ctor} needs SDK.Services.Commerce.CommerceClientConfiguration instance`;
            }
        };

        instanceStrict(
            commerceClientConfiguration,
            CommerceClientConfiguration,
            errorMessage.commerceClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {SDK.Services.Commerce.CommerceClientConfiguration}
         *
         */
        this.config = commerceClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.regionHeader = 'x-bamtech-region';

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(CommerceClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {String} namespace
     * @returns {Promise<SDK.Services.Commerce.GuidGeneratorResponse>}
     *
     */
    generateGuid(namespace) {

        const errorMessage = {
            method: `${this.constructor.name}.generateGuid(namespace)`,
            get namespace() {
                return `${this.method} namespace should be a String with length`;
            }
        };

        nonEmptyString(namespace, errorMessage.namespace, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            null,
            CommerceClientEndpoint.generateGuid,
            null,
            namespace
        );

        logger.info(CommerceClient.name, `Attempting to generate a guid for namespace: "${namespace}".`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.generateGuid,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { statusCode, statusMessage, token, region, sourceIP } = data;

            return Promise.resolve(
                new GuidGeneratorResponse({
                    statusCode,
                    statusMessage,
                    token,
                    region,
                    sourceIP
                })
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Commerce.PriceOrderRequest} request
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @returns {Promise<SDK.Services.Commerce.PriceOrderResponse>}
     *
     */
    priceOrder(request, accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.priceOrder(request, accessToken)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.PriceOrderRequest instance`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(request, PriceOrderRequest, errorMessage.request, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, httpClient, processOrderItems, logger } = this;

        const payload = this.getPayload(
            accessToken,
            CommerceClientEndpoint.priceOrder,
            request
        );

        logger.info(CommerceClient.name, 'Attempting a price order request.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.priceOrder,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { orderSubTotal, orderTaxAmount, orderTotalAmount, nextBillingDate, orderItems } = data;
            const { regionHeader } = this;

            const region = Util.getSafe(() => response.headers.get(regionHeader)) || undefined;
            const processedOrderItems = processOrderItems(orderItems);

            return Promise.resolve(
                new PriceOrderResponse({
                    orderSubTotal,
                    orderTaxAmount,
                    orderTotalAmount,
                    nextBillingDate,
                    orderItems: processedOrderItems,
                    region
                })
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} zipCode
     * @returns {Promise<Array<SDK.Services.Commerce.ZipLocation>>}
     *
     */
    lookupByZipCode(zipCode) {

        const errorMessage = {
            method: `${this.constructor.name}.lookupByZipCode(zipCode)`,
            get zipCode() {
                return `${this.method} zipCode should be a String with length`;
            }
        };

        nonEmptyString(zipCode, errorMessage.zipCode, InvalidArgumentException);

        const { dustEnabled, httpClient, processLocations, logger } = this;

        const payload = this.getPayload(
            null,
            CommerceClientEndpoint.lookupByZipCode,
            null,
            zipCode
        );

        logger.info(CommerceClient.name, `Retrieving locations by zipcode: "${zipCode}".`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.lookupByZipCode,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'lookupByZipCode');

        }).then((response) => {

            const { data } = response;
            const processedLocations = processLocations(data);

            return Promise.resolve(processedLocations);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Commerce.SubmitOrderRequest} request
     * @returns {Promise<Void>}
     *
     */
    submitOrder(request) {

        const errorMessage = {
            method: `${this.constructor.name}.submitOrder(request)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.SubmitOrderRequest instance`;
            }
        };

        instanceStrict(request, SubmitOrderRequest, errorMessage.request, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            null,
            CommerceClientEndpoint.submitOrder,
            request
        );

        logger.info(CommerceClient.name, 'Attempting to submit an order request.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.submitOrder,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Services.Commerce.SubmitOrderWithPaymentMethodRequest} request
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @returns {Promise<SDK.Services.Commerce.SubmitOrderWithPaymentMethodResponse>}
     */
    submitOrderWithPaymentMethod(request, accessToken) {

        const method = `${this.constructor.name}.submitOrderWithPaymentMethod(request, accessToken)`;
        const requestError = `${method} needs SDK.Services.Commerce.SubmitOrderWithPaymentMethodRequest instance`;
        const accessTokenError = `${method} needs SDK.Services.Token.AccessToken instance`;

        instanceStrict(request, SubmitOrderWithPaymentMethodRequest, requestError, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, accessTokenError, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            CommerceClientEndpoint.submitOrderWithPaymentMethod,
            request
        );

        logger.info(CommerceClient.name, 'Attempting to submit an order request with payment method.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.submitOrderWithPaymentMethod,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'submitOrderWithPaymentMethod');

        }).then((response) => {

            const { data } = response;
            const { guid } = data;

            return Promise.resolve(new SubmitOrderWithPaymentMethodResponse(guid));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} guid
     * @param {Number} counter
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @returns {Promise<SDK.Services.Commerce.OrderStatusResponse>}
     *
     */
    getOrderStatus(guid, counter, accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.getOrderStatus(guid, counter, accessToken)`,
            get guid() {
                return `${this.method} guid should be a String with length`;
            },
            get counter() {
                return `${this.method} counter should be a Number`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(guid, errorMessage.guid, InvalidArgumentException);
        number(counter, errorMessage.counter, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            CommerceClientEndpoint.getOrderStatus,
            null,
            {
                guid,
                counter
            }
        );

        logger.info(CommerceClient.name, `Attempting to retrieve the order status for guid: "${guid}".`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.getOrderStatus,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const statusCode = Util.getSafe(() => response.status);

            let orderStatusResponse = OrderStatusResponse.Undetermined;

            if (Check.equal(statusCode, 200)) {
                orderStatusResponse = OrderStatusResponse.Success;
            }

            return Promise.resolve(orderStatusResponse);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {Object} options
     * @param {SDK.Services.Commerce.CreatePaymentMethodRequest} options.request
     * @param {SDK.Services.Token.AccessToken} options.accessToken
     * @param {String} [options.region]
     * @note attempts to use `region`, if provided, to determine a region-specific endpoint to be used - if one isn't
     * found the JS SDK will use the default `createPaymentMethod` endpoint
     * @returns {Promise<SDK.Services.Commerce.CreatePaymentMethodResponse>}
     *
     */
    createPaymentMethod(options) {

        const errorMessage = {
            method: `${this.constructor.name}.createPaymentMethod(options)`,
            get options() {
                return `${this.method} options should be a non-empty Object`;
            },
            get request() {
                return `${this.method} needs SDK.Services.Commerce.CreatePaymentMethodRequest instance`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Commerce.CreatePaymentMethodRequest instance`;
            },
            get region() {
                return `${this.method} options.region should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { request, accessToken, region } = options;

        instanceStrict(request, CreatePaymentMethodRequest, request, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, accessToken, InvalidArgumentException);

        if (Check.assigned(region)) {
            nonEmptyString(region, errorMessage.region, InvalidArgumentException);
        }

        const { config, dustEnabled, httpClient, logger } = this;
        const { extras } = config;
        const { createPaymentMethodRegionalEndpoints = {} } = extras;

        const regionalEndpoint = createPaymentMethodRegionalEndpoints[region];
        const endpoint = regionalEndpoint ? CommerceClientEndpoint[regionalEndpoint] :
            CommerceClientEndpoint.createPaymentMethod;

        const payload = this.getPayload(
            accessToken,
            endpoint,
            request
        );

        logger.info(CommerceClient.name, 'Attempting to create payment method.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.createPaymentMethod,
            payload,
            method: HttpMethod.POST,
            data: {
                region
            }
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'createPaymentMethod');

        }).then((response) => {

            const id = Util.getSafe(() => response.data.paymentMethodId);
            const createPaymentMethodResponse = new CreatePaymentMethodResponse(id);

            return Promise.resolve(createPaymentMethodResponse);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @returns {Promise<SDK.Services.Commerce.PaymentMethodResponse>}
     *
     */
    getDefaultPaymentMethod(accessToken) {

        const method = `${this.constructor.name}.getDefaultPaymentMethod(accessToken)`;
        const accessTokenError = `${method} needs SDK.Services.Token.AccessToken instance`;

        instanceStrict(accessToken, AccessToken, accessTokenError, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            CommerceClientEndpoint.getDefaultPaymentMethod,
            null
        );

        logger.info(CommerceClient.name, 'Attempting to get default payment method.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.getDefaultPaymentMethod,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'getDefaultPaymentMethod');

        }).then((response) => {

            const { data } = response;

            const billingAddress = new BillingAddress(data.billingAddress);
            const options = Object.assign({}, data, { billingAddress });
            const paymentMethodResponse = new PaymentMethodResponse(options);

            return Promise.resolve(paymentMethodResponse);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} paymentMethodId
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @returns {Promise<SDK.Services.Commerce.PaymentMethodResponse>}
     *
     */
    getPaymentCard(paymentMethodId, accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.getPaymentCard(paymentMethodId, accessToken)`,
            get paymentMethodId() {
                return `${this.method} paymentMethodId should be a String with length`;
            },
            get accessToken() {
                return `${this.method} accessToken should be an instance of SDK.Services.Token.AccessToken`;
            }
        };

        nonEmptyString(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            CommerceClientEndpoint.getPaymentCard,
            null,
            paymentMethodId
        );

        logger.info(CommerceClient.name, 'Attempting to get payment card.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceClient.name,
            urn: CommerceClientDustUrnReference.getPaymentCard,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'getPaymentCard');

        }).then((response) => {

            const { data } = response;

            const billingAddress = new BillingAddress(data.billingAddress);
            const options = Object.assign({}, data, { billingAddress });
            const paymentMethodResponse = new PaymentMethodResponse(options);

            return Promise.resolve(paymentMethodResponse);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Array} orderItems
     * @returns {Array}
     *
     */
    processOrderItems(orderItems) {

        const processedOrderItems = [];

        for (const orderItem of orderItems) {

            const { sku, price } = orderItem;

            processedOrderItems.push(
                new OrderItem(
                    new Sku(sku),
                    new Price(price)
                )
            );

        }

        return processedOrderItems;

    }

    /**
     *
     * @access private
     * @param {Array} locations
     * @returns {Array}
     *
     */
    processLocations(locations) {

        const processedLocations = [];

        for (const location of locations) {

            processedLocations.push(
                new ZipLocation(
                    location.city,
                    location.state,
                    location.country
                )
            );

        }

        return processedLocations;

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {CommerceClientEndpoint} rel - endpoint type
     * @param {Object} body
     * @param {String|Object} param
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body, param) {

        const { endpoints } = this.config;

        const endpoint = endpoints[rel];

        const { href, headers } = endpoint;

        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        let url = href;
        let credentials;

        if (rel === CommerceClientEndpoint.generateGuid) {
            url = href.replace(/\{namespace\}/, param);
        }

        if (rel === CommerceClientEndpoint.lookupByZipCode) {
            url = href.replace(/\{zipcode\}/, param);
        }

        if (rel === CommerceClientEndpoint.getOrderStatus) {

            url = href.replace(/\{guid\}/, param.guid);
            url = url.replace(/\{counter\}/, param.counter);

            credentials = 'include';

        }

        if (rel === CommerceClientEndpoint.getPaymentCard) {
            url = href.replace(/\{paymentMethodId\}/, param);
        }

        if (Check.assigned(requestHeaders.Authorization)) {
            requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);
        }

        return {
            url,
            body: requestBody,
            headers: requestHeaders,
            credentials
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/commerce/commerceClient.js